import { identity, orderBy } from 'lodash';

export const negate = (value) => 0 - value;

export const getFilterCropAggregate = (data, cropFilter) => {
  return data?.cropAggregates?.find(({ crop }) => crop.name === cropFilter);
};

export const getGeneticStockBasisData = (data, cropFilter) => {
  const filterCropAggregate = getFilterCropAggregate(data, cropFilter);
  const activeCropAggregates = filterCropAggregate
    ? [filterCropAggregate]
    : data?.cropAggregates || [];
  return activeCropAggregates.map(({ crop, ecologicalSummary }) => ({
    ...crop,
    ...ecologicalSummary,
  }));
};

export const getSiteInfrastructureBasisData = ({ capexes }) =>
  orderBy(
    capexes.filter((capex) => capex.assignment === 'developer'),
    'baseTotalCost',
    ['desc'],
  );

const hasCarbon = ({ ecologicalSummary }) =>
  ecologicalSummary.carbonTonsByYear.some((v) => v > 0);

export const getCarbonData = ({ displayUnit: dbhUnit, transformToUnit }) => (
  data,
) => ({
  ...data,
  cropAggregates: data.cropAggregates
    .map((cropAggregate) => {
      const { crop, ecologicalSummary } = cropAggregate;
      if (!hasCarbon(cropAggregate)) return null;
      const { carbon } = crop;
      const { maxDbh } = carbon;
      return {
        crop,
        ...ecologicalSummary,
        ...carbon,
        dbhUnit,
        maxDbh: maxDbh && transformToUnit(maxDbh),
      };
    })
    .filter((c) => c),
});

export const getYieldData = (unitsMap) => ({ cropAggregates }) => ({
  cropAggregates: cropAggregates
    .filter(({ yieldAggregates }) => yieldAggregates[0])
    .map(({ yieldAggregates, crop }) => {
      const {
        yieldByYear,
        averageYieldAtMaturity,
        maxYield,
        yield: { unit, targetYieldPerTree, ...yieldRest },
        ...yieldAggregateRest
      } = yieldAggregates[0];
      const { displayUnit = unit, transformToUnit = identity } =
        unitsMap[unit] || {};
      return {
        crop,
        ...yieldRest,
        ...yieldAggregateRest,
        unit: displayUnit,
        yieldByYear: yieldByYear.map((y) => transformToUnit(y)),
        averageYieldAtMaturity: transformToUnit(averageYieldAtMaturity),
        maxYield: transformToUnit(maxYield),
        targetYieldPerTree: transformToUnit(targetYieldPerTree),
      };
    }),
});

export const getRevenueStreamData = (invertedRevenueUnitMap) => ({
  revenueStreamAggregates,
}) =>
  revenueStreamAggregates.map(
    ({
      revenueStream: { unit, avgUnitCost, filledUnitCost, ...stream },
      ...revenueAggregateRest
    }) => {
      const { transformToUnit = identity, displayUnit = unit } =
        invertedRevenueUnitMap[unit] || {};
      return {
        ...stream,
        ...revenueAggregateRest,
        // The unitPrice formatter puts in the slash, so don't include it here
        unit: displayUnit.replace('/', ''),
        // In the RevenueView we don't request filledUnitCost
        // so only transform the units if it exists
        filledUnitCost:
          filledUnitCost != null
            ? filledUnitCost.map((y) => transformToUnit(y))
            : undefined,
        // In the ProjectDashboard we don't request avgUnitCost
        // so only transform the units if it exists
        avgUnitCost:
          avgUnitCost != null ? transformToUnit(avgUnitCost) : undefined,
      };
    },
  );
